import type {
    CoverageDTO,
    TermDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { type FlosIconProps, Link, Table, FlosIcon } from '@flos/react-ui';
import CoverageCustomTableCell from './CoverageCustomTableCell';
import VaryingDeductableModal from '../VaryingDeductibleModal/VaryingDeductibleModal';
import { useRecoilValue } from 'recoil';
import CoverageTextModal from '../CoverageTextsModal/CoverageTextModal';
import { allHippoCoverageTextsSelector } from '../../recoil/selectors/allHippoCoverageTextsSelector';
import { convertLocalDateDTOToDate, convertLocalDateToString } from '../../utils/dateUtil';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { useHippoText } from '../../hooks/useHippoText';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';

const CoverageTableRow = ({
    coverage,
    iconProps,
    showValueCell = true,
    deductibleText,
    deductibleTerms,
    shouldAlignTextLeft,
    showEarlierEffectiveDates = false,
    showPrice,
    showActionButton
}: {
    coverage: CoverageDTO;
    iconProps?: FlosIconProps;
    showValueCell?: boolean;
    deductibleText?: string;
    deductibleTerms?: TermDTO[];
    shouldAlignTextLeft?: boolean;
    showEarlierEffectiveDates?: boolean;
    showPrice?: boolean;
    showActionButton?: React.JSX.Element;
}): React.JSX.Element => {
    const [showDeductibleModal, setShowDeductibleModal] = useState(false);
    const [showCoverageTextsModal, setShowCoverageTextsModal] = useState(false);
    const businessFlow = useRecoilValue(businessFlowSelector);
    const jobData = useRecoilValue(jobAtom);
    const coverageTextItems = useRecoilValue(allHippoCoverageTextsSelector);
    const foundTextItem = coverageTextItems?.find(
        (covText) =>
            covText.coverageItemId === coverage.type &&
            covText.termsId.some((id) => coverage.coverableTermId?.includes(id))
    );
    const coverageTextItem = foundTextItem
        ? { ...foundTextItem, title: coverage?.name ?? foundTextItem.title }
        : undefined;
    const getHippoText = useHippoText();

    const updateLaterEffectiveDateLabel = (coverageName: string, effectiveDate: string): string => {
        return (
            getHippoText('DSS-COVERABLE-DETAILS-COVERAGE-LATER-EFFECTIVE-DATE', {
                coverage: coverageName.toLowerCase(),
                date: effectiveDate
            }) ?? ''
        );
    };

    const getDisplayEarlierEffectiveDate = (coverage: CoverageDTO): string | undefined => {
        if (
            coverage.name &&
            coverage.effectiveDate &&
            jobData.quoteEffectiveDate &&
            convertLocalDateDTOToDate(coverage.effectiveDate) >
                convertLocalDateDTOToDate(jobData.quoteEffectiveDate)
        ) {
            const formattedDate = convertLocalDateToString(
                convertLocalDateDTOToDate(coverage.effectiveDate)
            );
            return formattedDate
                ? updateLaterEffectiveDateLabel(coverage.name, formattedDate)
                : undefined;
        }
        return undefined;
    };

    return (
        <>
            <Table.Row
                key={coverage.name}
                className={showActionButton ? 'u-flex-box@xs u-flex-box--col@xs u-gap-none@xs' : ''}
            >
                <CoverageCustomTableCell>
                    <div className="u-display-flex u-gap-3 u-flex-justify-between u-flex-justify-start@xs u-flex-items-center">
                        {iconProps && (
                            <FlosIcon
                                {...iconProps}
                                className={
                                    (iconProps.className ?? '') +
                                    ' ' +
                                    'u-display-none u-display-block@xs flos-icon-start'
                                }
                            />
                        )}
                        {coverageTextItem ? (
                            <Link
                                onClick={() => {
                                    setShowCoverageTextsModal(true);
                                }}
                                className="u-color-primary-2-base u-Normalwrap"
                            >
                                {coverage.name}
                            </Link>
                        ) : (
                            <span className="u-Normalwrap">{coverage.name}</span>
                        )}

                        {iconProps && (
                            <FlosIcon
                                {...iconProps}
                                className={
                                    (iconProps.className ?? '') +
                                    ' ' +
                                    'u-display-none@xs flos-icon-end'
                                }
                            />
                        )}
                    </div>
                    <span className="u-Normalwrap">
                        {showEarlierEffectiveDates && getDisplayEarlierEffectiveDate(coverage)}
                    </span>
                </CoverageCustomTableCell>
                {showValueCell && deductibleText && (
                    <CoverageCustomTableCell
                        hideDataHeader={showValueCell}
                        dataHeader="Selvrisiko"
                        className={shouldAlignTextLeft ? 'u-text-right' : 'u-text-right@xs'}
                    >
                        {deductibleText === 'Varierende selvrisiko' ? (
                            <Link
                                onClick={() => {
                                    setShowDeductibleModal(true);
                                }}
                                className="u-color-primary-2-base u-Normalwrap"
                            >
                                {deductibleText}
                            </Link>
                        ) : (
                            <span className="u-Normalwrap">{deductibleText}</span>
                        )}
                    </CoverageCustomTableCell>
                )}
                {showPrice && showActionButton && businessFlow !== 'OPC' && (
                    <CoverageCustomTableCell
                        hideDataHeader={showValueCell}
                        className={shouldAlignTextLeft ? 'u-text-right' : 'u-text-right@xs'}
                    >
                        {showActionButton}
                    </CoverageCustomTableCell>
                )}
            </Table.Row>
            {showDeductibleModal &&
                createPortal(
                    <VaryingDeductableModal
                        closeModal={() => {
                            setShowDeductibleModal(false);
                        }}
                        showModal={showDeductibleModal}
                        deductibleTerms={deductibleTerms ?? []}
                        coverage={coverage}
                    />,
                    document.body
                )}
            {showCoverageTextsModal &&
                coverageTextItem &&
                createPortal(
                    <CoverageTextModal
                        closeModal={() => {
                            setShowCoverageTextsModal(false);
                        }}
                        showModal={showCoverageTextsModal}
                        coverageTextItem={coverageTextItem}
                    />,
                    document.body
                )}
        </>
    );
};

export default CoverageTableRow;
